import {
  Button,
  DialogContentText,
  makeStyles,
  Typography
} from "@material-ui/core";
import Card from "@material-ui/core/Card";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SearchBar from "@saleor/components/SearchBar";
import { useBulkVoucherListDownload } from "@saleor/discounts/queries";
import { BulkVoucherList_voucherGroup_vouchers_edges_node } from "@saleor/discounts/types/BulkVoucherList";
import {
  voucherListUrl,
  VoucherListUrlQueryParams,
  voucherUrl
} from "@saleor/discounts/urls";
import { voucherDownload } from "@saleor/discounts/utils/utils";
import { getFilterVariables } from "@saleor/discounts/views/VoucherList/filters";
import useDownloadCsv from "@saleor/hooks/useDownloadCsv";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { FilterPageProps, ListActions, PageListProps } from "@saleor/types";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import BulkVoucherList from "../BulkVoucherList/bulkVoucherList";
import { VoucherFilterKeys, VoucherListFilterOpts } from "../VoucherListPage";

const useStyles = makeStyles(
  theme => ({
    backBtn: {
      "&:hover": {
        color: "#0c76f7"
      },
      alignItems: "center",
      color: "#000000",
      cursor: "pointer",
      display: "inline-flex",
      marginBottom: "15px",
      marginTop: "30px",
      textDecoration: "none"
    },
    buttonContainers: {
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between"
    },
    exportBtnText: {
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 600 as 600,
      textTransform: "uppercase"
    },
    filterButton: {
      border: "2px solid #1848FE"
    },
    label: {
      "&:hover": {
        color: "#0c76f7"
      },
      cursor: "pointer",
      fontSize: "20px !important",
      marginLeft: "10px"
    }
  }),
  {
    name: "bulkvoucherlistpage"
  }
);

export interface BulkVoucherListPageProps
  extends PageListProps,
    ListActions,
    FilterPageProps<VoucherFilterKeys, VoucherListFilterOpts> {
  defaultCurrency: string;
  vouchers: BulkVoucherList_voucherGroup_vouchers_edges_node[];
  bulkVoucher: string;
  bulkID: string;
  params: VoucherListUrlQueryParams;
  paginationState;
}

const BulkVoucherListPage: React.FC<BulkVoucherListPageProps> = props => {
  const {
    currencySymbol,
    bulkID,
    params,
    bulkVoucher,
    currentTab,
    initialSearch,
    onSearchChange,
    onTabChange,
    onTabDelete,
    onTabSave,
    paginationState,
    tabs,
    ...listProps
  } = props;
  const classes = useStyles(props);
  // const history = useHistory();
  const navigate = useNavigator();

  const intl = useIntl();
  const notify = useNotifier();

  const [downloadPagination, setDownloadPagination] = useState("");
  const [loadData, setLoadData] = useState(true);
  const [downloadData, setDownloadData] = useState([]);
  const [bulkGroupID, setBulkGroupID] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const dateNow = Moment(new Date()).format("_YYYYMMDD_HHmmss");
  const fileName = `${voucherDownload.bulkVoucherDownload.filename}${dateNow}`;

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 100,
      id: bulkGroupID
    }),
    [params, bulkGroupID]
  );

  const {
    data: bulkVoucherData,
    loading: bulkVoucherDataLoading,
    refetch: refetchDownload
  } = useBulkVoucherListDownload({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    setBulkGroupID(null);
  };

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;
      delete data.used;
      data.groupCode = data?.group?.code;
      data.minSpent =
        data?.minSpent?.amount > 0 ? data?.minSpent?.amount : "None";
      data.endDate = data?.endDate
        ? Moment.tz(new Date(data?.endDate), "Asia/Manila").format("YYYY-MM-DD")
        : null;
      data.startDate = data?.startDate
        ? Moment.tz(new Date(data?.startDate), "Asia/Manila").format(
            "YYYY-MM-DD"
          )
        : null;
      data.isHidden = data?.isHidden ? "Yes" : "No";
      data.applyOncePerCustomer = data?.applyOncePerCustomer ? "Yes" : "No";
      data.isLimitToNewCustomer = data?.isLimitToNewCustomer ? "Yes" : "No";
      data.usageLimit = data?.usageLimit ? data?.usageLimit : "None";

      return data;
    });
    return selectedData;
  };

  const allVoucherGroup = bulkVoucherData?.voucherGroup?.vouchers?.edges?.map(
    edge => edge.node
  );
  const nextPage = bulkVoucherData?.voucherGroup?.vouchers?.pageInfo;

  const breadcrumbDirectory = [
    {
      name: "Vouchers",
      url: "/discounts/vouchers"
    },
    {
      name: bulkVoucher
    }
  ];

  React.useEffect(() => {
    if (bulkVoucherData && bulkGroupID && !bulkVoucherDataLoading) {
      setDownloadData(current => [...current, allVoucherGroup]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadCsv(
          selectedReportData,
          voucherDownload.bulkVoucherDownload.fields,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher group successfully downloaded."
          })
        });
        setBulkGroupID(null);
      }
    } else if (bulkGroupID) {
      refetchDownload();
    }
  }, [bulkVoucherData, bulkVoucherDataLoading]);

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div style={{ display: "flex", flexDirection: "row", marginTop: "10px" }}>
        <PageBackNavigation
          onBack={() =>
            navigate(
              voucherListUrl({
                ...params,
                activeTab: "1"
              })
            )
          }
        />
        <PageHeader title={bulkVoucher} />
        <div style={{ marginLeft: "auto" }}>
          <Button
            id="exportVouchersBtn"
            variant="outlined"
            className={classes.filterButton}
            onClick={() => setBulkGroupID(bulkID)}
          >
            <Typography className={classes.exportBtnText}>
              <FormattedMessage
                defaultMessage="Export Vouchers"
                description="button"
              />
            </Typography>
          </Button>
        </div>
      </div>
      <div className={classes.buttonContainers}></div>
      <Card>
        <SearchBar
          initialSearch={initialSearch}
          searchPlaceholder={intl.formatMessage({
            defaultMessage: "Search Voucher Code"
          })}
          tabs={[]}
          onSearchChange={onSearchChange}
          onTabChange={onTabChange}
          onTabDelete={onTabDelete}
          onTabSave={onTabSave}
          currentTab={0}
          showTab={true}
        />
        <BulkVoucherList
          {...listProps}
          onRowClick={id => () => navigate(voucherUrl(id))}
        />
      </Card>
      <ActionDialog
        open={bulkGroupID != null}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
          refetchDownload();
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Voucher Group List",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage defaultMessage="You are about to download a CSV file, Do you want to continue?" />
        </DialogContentText>
      </ActionDialog>
    </Container>
  );
};
BulkVoucherListPage.displayName = "BulkVoucherListPage";
export default BulkVoucherListPage;
